import { Link } from 'react-router-dom';
import HeroBackground from '../../components/HeroBackground';
import RotatingWord from '../../components/RotatingWord';
import Reveal from '../../components/Reveal';

const SCRIM =
  'linear-gradient(90deg, color-mix(in srgb, var(--color-bg) 88%, transparent) 0%, color-mix(in srgb, var(--color-bg) 50%, transparent) 44%, color-mix(in srgb, var(--color-bg) 0%, transparent) 80%),' +
  'linear-gradient(0deg, var(--color-bg) 2%, color-mix(in srgb, var(--color-bg) 0%, transparent) 46%)';

const CAPABILITIES = [
  {
    n: '01',
    title: 'Product & web applications',
    body: 'Nearly a decade shipping web applications, products, internal tools, and services that hold up under real users and deliver a quality user experience.'
  },
  {
    n: '02',
    title: 'Infrastructure & data systems',
    body: 'Vehicle-data platforms, physical mobile device automations, and distributed pipelines moving billions of records. I design scalable, observable systems that scales and delivers quality user experiences.'
  },
  {
    n: '03',
    title: 'Reverse engineering',
    body: 'Undocumented APIs, brick-walled services, mobile apps, binaries — I work out how they actually behave, then integrate with them, rebuild them, or harden yours.'
  },
  {
    n: '04',
    title: 'Scraping & automation',
    body: 'Collection and integration from almost any source — sites, mobile apps, internal systems. Scattered data and manual processes go in; clean feeds and pipelines come out, landing exactly where your team needs them.'
  }
];

const Home = () => {
  return (
    <main>
      {/* HERO */}
      <section className="relative flex min-h-[100svh] flex-col justify-end overflow-hidden pt-[78px]">
        <HeroBackground />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 z-[1]"
          style={{ background: SCRIM }}
        />
        <div className="relative z-[2] w-full px-5 pb-[76px] sm:px-6 md:px-12">
          <p className="hero-rise text-fg-faint max-w-[19ch] text-balance font-normal leading-[1.2] tracking-[-0.03em] text-[clamp(33px,4.5vw,56px)]">
            Building{' '}
            <strong className="text-fg font-semibold">
              world-class <RotatingWord />
            </strong>{' '}
            for teams who need it <em className="text-fg font-medium italic">done properly</em>.
          </p>
          <div className="hero-rise--late mt-[52px] flex flex-wrap items-center gap-[14px]">
            <Link
              to="/contact"
              className="btn btn--lg w-full justify-center sm:w-auto sm:justify-start">
              Start a conversation <span className="arr">↗</span>
            </Link>
            <a href="mailto:hello@hvdm.io" className="btn btn--ghost btn--lg hidden md:inline-flex">
              hello@hvdm.io
            </a>
          </div>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section className="section">
        <div className="wrap">
          <div className="sec-index">
            <span className="label whitespace-nowrap">expertise</span>
          </div>
          <Reveal>
            <div className="border-line border-t">
              {CAPABILITIES.map((c) => (
                <div
                  key={c.n}
                  className="group grid grid-cols-[52px_1fr] items-baseline gap-x-[18px] gap-y-1.5 border-b border-line-faint py-7 transition-[padding-left] duration-[450ms] ease-out hover:pl-[18px] md:grid-cols-[96px_minmax(0,1fr)_1.05fr] md:gap-12 md:py-[42px]">
                  <span className="text-line tabular-nums leading-none font-light tracking-[-0.02em] text-[26px] transition-colors duration-[350ms] group-hover:text-fg-faint md:text-[42px]">
                    {c.n}
                  </span>
                  <h3 className="h3">{c.title}</h3>
                  <p className="text-fg-dim col-start-2 mt-2 max-w-[46ch] text-base leading-[1.55] md:col-span-1 md:col-start-auto md:mt-0">
                    {c.body}
                  </p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* TRACK RECORD */}
      <section className="section">
        <div className="wrap">
          <Reveal>
            <div className="grid items-end gap-10 md:grid-cols-[1fr_0.85fr] md:gap-16">
              <p className="max-w-[22ch] text-[clamp(24px,2.8vw,38px)] font-medium leading-[1.3] tracking-[-0.02em]">
                A career spent building software for teams who need it done properly.
              </p>
              <div className="border-line-faint flex flex-col gap-px border-y bg-line-faint">
                <a
                  href="https://github.com/hrvdm"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-bg group flex items-baseline justify-between gap-5 px-0.5 py-5 transition-[padding,color] duration-[350ms] hover:px-2.5 hover:text-fg">
                  <span className="label">GitHub</span>
                  <span className="text-fg-dim text-[17px] font-medium transition-colors duration-[250ms] group-hover:text-fg">
                    /hrvdm ↗
                  </span>
                </a>
                <a
                  href="https://www.linkedin.com/in/hrvdm/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-bg group flex items-baseline justify-between gap-5 px-0.5 py-5 transition-[padding,color] duration-[350ms] hover:px-2.5 hover:text-fg">
                  <span className="label">LinkedIn</span>
                  <span className="text-fg-dim text-[17px] font-medium transition-colors duration-[250ms] group-hover:text-fg">
                    /hrvdm ↗
                  </span>
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="border-line-faint border-t py-[150px] text-center">
        <div className="wrap">
          <h2 className="display mx-auto mt-2 max-w-[18ch] leading-[0.94] tracking-[-0.045em]">
            <span className="text-fg-faint font-medium">Ready to build something</span>
            <br />
            <em className="font-semibold italic">f*cking</em> awesome?
          </h2>
          <p className="lead mx-auto mt-[26px] max-w-[46ch]">
            Let's talk! I'll tell you if I'm the right person for your project and how I can best
            work with you or your team.
          </p>
          <div className="mt-12 flex flex-wrap justify-center gap-[14px]">
            <Link to="/contact" className="btn btn--lg w-full justify-center sm:w-auto">
              Start a conversation <span className="arr">↗</span>
            </Link>
            <a href="mailto:hello@hvdm.io" className="btn btn--ghost btn--lg hidden md:inline-flex">
              hello@hvdm.io
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Home;
